import { CardElement } from "../types/battle"
import { ActiveTimeProgress, GuardianRankInfo, PetView } from "../types/pet"
import { ELEMENT_COLORS } from "./guardians"

/** Акцент питомца до пробуждения / выбора стихии */
export const PET_NEUTRAL_ACCENT = "rgba(255,255,255,0.45)"

/** 15 ч активного времени до пробуждения */
export const PET_AWAKEN_REQUIRED_SEC = 54000

/** Daily cap стажа: 90 мин */
export const PET_DAILY_CAP_SEC = 5400

export const PET_AWAKEN_RANK = 10
export const GUARDIAN_MAX_COMBAT_RANK = 20
export const GUARDIAN_MAX_RANK = 100

export const PET_MAX_LEVEL = 20
export const PET_MAX_XP = 8000

export const PET_PERK_LEVELS = [7, 12] as const
export const PET_PERK_BATTLE_XP = { 7: 600, 12: 1800 } as const

export const PET_RESPEC_CHAKRA_COST = 500
export const PET_RENAME_COOLDOWN_DAYS = 30
export const PET_RESPEC_COOLDOWN_DAYS = 30

export const PET_DEFAULT_NAMES: Record<CardElement, string> = {
  FIRE: "Огненный лев",
  WATER: "Синий тигр",
  EARTH: "Пещерный носорог",
  LIGHTNING: "Электрический гепард",
  WIND: "Облачный грифон",
  ICE: "Ледяной волк",
}

export const PET_FORM_LABELS: Record<string, string> = {
  "1-2": "Детёныш",
  "3-4": "Подросток",
  "5-9": "Взрослый",
  "10-11": "Соратник",
  "12-14": "Древний",
  "15-19": "Хранитель",
  "20": "Легенда",
}

export const COSMETIC_LABELS: Record<string, string> = {
  RANK_ICON_5: "Иконка ранга V",
  PASSIVE_BOOST_RANK_8: "Усиление пассива",
  PET_AWAKEN: "Пробуждение питомца",
  PET_FORM_12: "Форма «Древний»",
  DUST_BONUS_RANK_15: "+2% пыли стихии",
  COMBAT_CAP_RANK_20: "Макс. боевой ранг",
  FRAME_XXV: "Рамка профиля XXV",
  TITLE_VETERAN_30: "Титул",
  FRAME_II_35: "Рамка II",
  CARD_AURA_I_40: "Aura на карте I",
  CARD_AURA_II_50: "Aura II",
  FRAME_III_60: "Рамка III",
  CARD_AURA_III_75: "Aura III",
  FRAME_LEGEND_90: "Рамка легенда",
  AURA_ULTIMATE_100: "Ultimate aura",
}

export type PetUiState = "sleeping" | "ready" | "awakened"

export function resolvePetUiState(
  spiritAwakened: boolean,
  canAwaken: boolean
): PetUiState {
  if (spiritAwakened) return "awakened"
  if (canAwaken) return "ready"
  return "sleeping"
}

export function formatActiveTimeHoursMinutes(totalSec: number): string {
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  return `${hours}ч ${minutes}мин`
}

export function formatAwakenProgress(activeTime: ActiveTimeProgress): string {
  const total = formatActiveTimeHoursMinutes(activeTime.activeTimeTotalSec)
  const required = formatActiveTimeHoursMinutes(activeTime.unlockRequiredSec)
  return `Стаж: ${total} / ${required}`
}

export function formatDailyActiveTime(activeTime: ActiveTimeProgress): string {
  const todayMin = Math.floor(activeTime.activeTimeTodaySec / 60)
  const capMin = Math.floor(activeTime.dailyCapSec / 60)
  return `Сегодня: ${todayMin} / ${capMin} мин`
}

export function needsPetPerkChoice(
  pet: Pick<PetView, "level" | "perkLevel7" | "perkLevel12" | "battleXpTotal"> | null | undefined
): {
  level7: boolean
  level12: boolean
} {
  if (!pet) return { level7: false, level12: false }
  return {
    level7:
      pet.level >= 7 &&
      !pet.perkLevel7 &&
      pet.battleXpTotal >= PET_PERK_BATTLE_XP[7],
    level12:
      pet.level >= 12 &&
      !pet.perkLevel12 &&
      pet.battleXpTotal >= PET_PERK_BATTLE_XP[12],
  }
}

export function hasPetNotificationBadge(
  canAwaken: boolean,
  pet: Pick<PetView, "level" | "perkLevel7" | "perkLevel12" | "battleXpTotal"> | null | undefined
): boolean {
  if (canAwaken) return true
  const perks = needsPetPerkChoice(pet)
  return perks.level7 || perks.level12
}

export function preAwakenPassiveHint(mult: number, activeTime?: ActiveTimeProgress | null): string {
  if (mult >= 1) {
    return "Полная сила стихии. Пробуди питомца для новых бонусов."
  }
  if (activeTime) {
    const remaining = Math.max(0, activeTime.unlockRequiredSec - activeTime.activeTimeTotalSec)
    const remLabel = formatActiveTimeHoursMinutes(remaining)
    return `Дух спит — сила стихии ослаблена. Осталось ${remLabel} до пробуждения.`
  }
  return "Дух спит — сила стихии ослаблена."
}

export function formatGuardianRankLine(info: GuardianRankInfo): string {
  return `Ранг хранителя: ${info.rank}`
}

export function getPetFormLabelForLevel(level: number): string {
  const lv = Math.max(1, Math.min(PET_MAX_LEVEL, Math.floor(level)))
  if (lv >= 20) return "Легенда"
  if (lv >= 15) return "Хранитель"
  if (lv >= 12) return "Древний"
  if (lv >= 10) return "Соратник"
  if (lv >= 5) return "Взрослый"
  if (lv >= 3) return "Подросток"
  return "Детёныш"
}

export const PET_FORM_LEVEL_SAMPLES = [1, 3, 5, 10, 12, 15, 20] as const

export const PET_UI_STATE_LABELS: Record<PetUiState, string> = {
  sleeping: "Сон (стаж < 15ч)",
  ready: "Готов к пробуждению",
  awakened: "Пробуждён",
}

export function resolvePetElementFromPassport(passport: {
  pet?: { element: CardElement } | null
  spiritAwakened?: boolean
  affiliationElement?: CardElement | null
}): CardElement | null {
  if (passport.pet?.element) return passport.pet.element
  if (passport.spiritAwakened && passport.affiliationElement) {
    return passport.affiliationElement
  }
  return null
}

export function resolvePetAccentColor(
  element: CardElement | null | undefined
): string {
  return element ? ELEMENT_COLORS[element] : PET_NEUTRAL_ACCENT
}

export function petAccentSurface(element: CardElement | null | undefined): {
  borderColor: string
  backgroundColor: string
} {
  if (!element) {
    return {
      borderColor: "rgba(255,255,255,0.1)",
      backgroundColor: "rgba(255,255,255,0.06)",
    }
  }
  const color = ELEMENT_COLORS[element]
  return {
    borderColor: `${color}55`,
    backgroundColor: `${color}14`,
  }
}
